import { useState, useEffect } from "react";
import { Eye, EyeOff, Link2, Settings } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Switch } from "../ui/switch";

interface Column {
  field: string;
  label: string;
  type?: string;
  visible?: boolean;
  lookupTable?: {
    tableId: string;
    keyField: string;
    displayField: string;
  };
}

interface ColumnConfigModalProps {
  columns: Column[];
  onClose: () => void;
  onSave: (columns: Column[]) => void;
  availableTables?: any[]; // Tabelas disponíveis para lookup
}

export default function ColumnConfigModal({
  columns,
  onClose,
  onSave,
  availableTables = [],
}: ColumnConfigModalProps) {
  const [localColumns, setLocalColumns] = useState<Column[]>([]);
  const [selectedColumn, setSelectedColumn] = useState<string | null>(null);

  useEffect(() => {
    setLocalColumns(JSON.parse(JSON.stringify(columns)));
  }, [columns]);

  const selectedCol = localColumns.find((col) => col.field === selectedColumn);

  const toggleVisibility = (field: string) => {
    setLocalColumns((prev) =>
      prev.map((col) =>
        col.field === field ? { ...col, visible: !col.visible } : col
      )
    );
  };

  const updateColumnType = (field: string, type: string) => {
    setLocalColumns((prev) =>
      prev.map((col) => {
        if (col.field === field) {
          const updated = { ...col, type };
          // Limpar configuração de lookup se não for tipo lookup
          if (type !== 'lookup') {
            delete updated.lookupTable;
          }
          return updated;
        }
        return col;
      })
    );
  };

  const updateLookupConfig = (field: string, config: any) => {
    setLocalColumns((prev) =>
      prev.map((col) =>
        col.field === field
          ? { ...col, lookupTable: { ...col.lookupTable, ...config } }
          : col
      )
    );
  };

  const handleSave = () => {
    onSave(localColumns);
    onClose();
  };

  const selectedLookupTable = availableTables.find(
    (t) => t.id === selectedCol?.lookupTable?.tableId
  );

  const lookupTableColumns = selectedLookupTable?.config?.columns || [];

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-5xl max-h-[85vh]">
        <DialogHeader>
          <DialogTitle>Configurar Colunas</DialogTitle>
          <DialogDescription>
            Gerencie visibilidade, tipos e relacionamentos das colunas
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-4 flex-1 overflow-hidden">
          {/* Lista de Colunas */}
          <div className="w-1/3 border rounded-lg overflow-hidden flex flex-col">
            <div className="bg-muted px-4 py-3 border-b">
              <h3 className="font-semibold">Colunas</h3>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-1">
              {localColumns.map((col) => (
                <button
                  key={col.field}
                  onClick={() => setSelectedColumn(col.field)}
                  className={`w-full text-left p-3 rounded-md transition-colors flex items-center justify-between ${
                    selectedColumn === col.field
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-muted"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {col.visible !== false ? (
                      <Eye className="w-4 h-4" />
                    ) : (
                      <EyeOff className="w-4 h-4 opacity-50" />
                    )}
                    <span className="text-sm font-medium">{col.label}</span>
                  </div>
                  {col.type === 'lookup' && (
                    <Link2 className="w-4 h-4" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Configuração da Coluna Selecionada */}
          <div className="flex-1 border rounded-lg overflow-hidden flex flex-col">
            {selectedCol ? (
              <>
                <div className="bg-accent px-4 py-3 border-b border-primary/20">
                  <div className="flex items-center gap-2">
                    <Settings className="w-4 h-4 text-primary" />
                    <h3 className="font-semibold">{selectedCol.label}</h3>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {/* Visibilidade */}
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-md">
                    <Label htmlFor="visible" className="cursor-pointer">
                      Coluna Visível
                    </Label>
                    <Switch
                      id="visible"
                      checked={selectedCol.visible !== false}
                      onCheckedChange={() => toggleVisibility(selectedCol.field)}
                    />
                  </div>

                  {/* Tipo */}
                  <div className="space-y-2">
                    <Label htmlFor="columnType">Tipo da Coluna</Label>
                    <Select
                      value={selectedCol.type || 'text'}
                      onValueChange={(value) =>
                        updateColumnType(selectedCol.field, value)
                      }
                    >
                      <SelectTrigger id="columnType">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="text">Texto</SelectItem>
                        <SelectItem value="number">Número</SelectItem>
                        <SelectItem value="date">Data</SelectItem>
                        <SelectItem value="badge">Badge</SelectItem>
                        <SelectItem value="id">ID</SelectItem>
                        <SelectItem value="lookup">🔗 Lookup (Foreign Key)</SelectItem>
                        <SelectItem value="tableLink">📊 Link para Planilha (Google Sheets)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Configuração de Lookup */}
                  {selectedCol.type === 'lookup' && (
                    <div className="space-y-4 p-4 border rounded-lg bg-accent/50">
                      <div className="flex items-center gap-2 mb-2">
                        <Link2 className="w-5 h-5 text-primary" />
                        <h4 className="font-semibold">Configuração de Lookup</h4>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="lookupTable">Tabela Referenciada</Label>
                        <Select
                          value={selectedCol.lookupTable?.tableId || ''}
                          onValueChange={(value) =>
                            updateLookupConfig(selectedCol.field, { tableId: value })
                          }
                        >
                          <SelectTrigger id="lookupTable">
                            <SelectValue placeholder="Selecione uma tabela" />
                          </SelectTrigger>
                          <SelectContent>
                            {availableTables.map((table) => {
                              const pageName = (table as any)._pageName;
                              const displayName = pageName
                                ? `${table.config.title} (${pageName})`
                                : table.config.title;
                              return (
                                <SelectItem key={table.id} value={table.id}>
                                  {displayName}
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          Tabela que contém os dados de referência
                        </p>
                      </div>

                      {selectedCol.lookupTable?.tableId && (
                        <>
                          <div className="space-y-2">
                            <Label htmlFor="keyField">Campo Chave (ID)</Label>
                            <Select
                              value={selectedCol.lookupTable?.keyField || ''}
                              onValueChange={(value) =>
                                updateLookupConfig(selectedCol.field, { keyField: value })
                              }
                            >
                              <SelectTrigger id="keyField">
                                <SelectValue placeholder="Selecione o campo ID" />
                              </SelectTrigger>
                              <SelectContent>
                                {lookupTableColumns.map((col: any) => (
                                  <SelectItem key={col.field} value={col.field}>
                                    {col.label} ({col.field})
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <p className="text-xs text-muted-foreground">
                              Campo usado para fazer o match (ex: id)
                            </p>
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="displayField">Campo a Exibir</Label>
                            <Select
                              value={selectedCol.lookupTable?.displayField || ''}
                              onValueChange={(value) =>
                                updateLookupConfig(selectedCol.field, {
                                  displayField: value,
                                })
                              }
                            >
                              <SelectTrigger id="displayField">
                                <SelectValue placeholder="Selecione o campo a mostrar" />
                              </SelectTrigger>
                              <SelectContent>
                                {lookupTableColumns.map((col: any) => (
                                  <SelectItem key={col.field} value={col.field}>
                                    {col.label} ({col.field})
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <p className="text-xs text-muted-foreground">
                              Valor que será exibido na tabela (ex: nome, status)
                            </p>
                          </div>
                        </>
                      )}

                      <div className="p-3 bg-primary/10 border border-primary/20 rounded-md">
                        <p className="text-xs">
                          <strong>💡 Como funciona:</strong> Esta coluna armazena o ID, mas exibe o valor da tabela referenciada.
                          Exemplo: coluna "statusId" guarda "1", mas mostra "Ativo".
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <Settings className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Selecione uma coluna para configurar</p>
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={handleSave}>Salvar Configurações</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
